import { useEffect, useRef, useState } from "react";
import StaticDropdown from "src/Components/Dropdown/StaticDropdown";
import { OrderFilterOptions } from "src/Interfaces/dropdownInterfaces";
import { packetErrorHandler } from "src/Utils/requestUtils";

interface Props {
  getFilterOptions: () => Promise<OrderFilterOptions>;
  searchParam: {
    portfolioGroupId?: number | null;
    instrumentId?: string | number | null;
    [name: string]: any;
  };
  accountField: "sahraUserId" | "sahraUserid";
  onChange: (name: string, value: any) => void;
  instrumentLabel?: string;
}

const emptyOptions: OrderFilterOptions = {
  portfolioGroups: [],
  sahraUsers: [],
  instruments: [],
};

export default function OrderTableFilters({
  getFilterOptions,
  searchParam,
  accountField,
  onChange,
  instrumentLabel = "نماد",
}: Props) {
  const [options, setOptions] = useState(emptyOptions);
  const labels = useRef(new Map<string, Map<string, string>>());

  useEffect(() => {
    let disposed = false;
    let timer: ReturnType<typeof setTimeout>;
    // Keep the current labels visible until the replacement options arrive.

    const refresh = async () => {
      try {
        const next = await getFilterOptions();
        if (disposed) return;
        for (const [kind, items] of [
          ["groups", next.portfolioGroups],
          ["accounts", next.sahraUsers],
          [instrumentLabel, next.instruments],
        ] as const) {
          const known = labels.current.get(kind) ?? new Map<string, string>();
          items.forEach((item) => known.set(String(item.id), item.name));
          labels.current.set(kind, known);
        }
        // A selection from another tab/history window must not invisibly filter this table.
        for (const [name, items] of [
          ["portfolioGroupId", next.portfolioGroups],
          [accountField, next.sahraUsers],
          ["instrumentId", next.instruments],
        ] as const) {
          const selected = searchParam[name];
          if (selected != null && selected !== "" && !items.some((item) => String(item.id) === String(selected))) {
            onChange(name, null);
          }
        }
        setOptions((previous) =>
          JSON.stringify(previous) === JSON.stringify(next) ? previous : next,
        );
      } catch (error) {
        if (!disposed) packetErrorHandler(error);
      } finally {
        // Refresh along with the live tables, including history changes from another tab.
        if (!disposed) timer = setTimeout(refresh, 10000);
      }
    };
    refresh();
    return () => {
      disposed = true;
      clearTimeout(timer);
    };
  }, [getFilterOptions, accountField, searchParam, onChange, instrumentLabel]);

  const filters = [
    { name: "portfolioGroupId", label: "گروه پرتفو", kind: "groups", items: options.portfolioGroups },
    { name: accountField, label: "اکانت", kind: "accounts", items: options.sahraUsers },
    { name: "instrumentId", label: instrumentLabel, kind: instrumentLabel, items: options.instruments },
  ];

  return (
    <div className="flex flex-row justify-start items-center gap-3">
      {filters.map(({ name, label, kind, items }) => {
        const selected = searchParam[name];
        const selectedLabel = labels.current.get(kind)?.get(String(selected));
        // Retain only the selected label from a previous visit while this tab loads.
        const displayItems = selectedLabel && !items.some((item) => String(item.id) === String(selected))
          ? [...items, { id: selected, name: selectedLabel }]
          : items;
        return (
        <div className="w-48" key={name}>
          <StaticDropdown
            dropdown={{ name, placeholder: label }}
            options={displayItems}
            value={searchParam[name]}
            onChange={(field, value) => {
              if (value == null || value === "") {
                onChange(field, null);
              } else {
                onChange(field, field === "instrumentId" ? String(value) : Number(value));
              }
            }}
          />
        </div>
        );
      })}
    </div>
  );
}
